import type { GatewayHelloOk } from "../gateway.ts";
import type { SessionsListResult } from "../types.ts";
import { isSubagentSessionKey, parseAgentSessionKey } from "../../../../src/routing/session-key.js";

type SessionDefaultsSnapshot = {
  mainSessionKey?: string;
  mainKey?: string;
};

/** Gateway-advertised main session key, when present. */
export function resolveMainSessionKey(
  hello: GatewayHelloOk | null,
  sessions: SessionsListResult | null,
): string | null {
  const snapshot = hello?.snapshot as { sessionDefaults?: SessionDefaultsSnapshot } | undefined;
  const mainSessionKey = snapshot?.sessionDefaults?.mainSessionKey?.trim();
  if (mainSessionKey) {
    return mainSessionKey;
  }
  const mainKey = snapshot?.sessionDefaults?.mainKey?.trim();
  if (mainKey) {
    return mainKey;
  }
  if (sessions?.sessions?.some((row) => row.key === "main")) {
    return "main";
  }
  const agentMain = sessions?.sessions?.find((row) => {
    const parsed = parseAgentSessionKey(row.key);
    return parsed?.rest === "main" && !isSubagentSessionKey(row.key);
  });
  return agentMain?.key ?? null;
}

/**
 * Delegation (sessions_spawn) must run from a main session, not a subagent thread.
 * When the user is on a subagent session, pick the best main session to use instead.
 */
export function pickMainSessionForDelegation(
  hello: GatewayHelloOk | null,
  sessions: SessionsListResult | null,
  currentSessionKey: string,
  defaultAgentId = "main",
): string {
  if (!isSubagentSessionKey(currentSessionKey)) {
    return currentSessionKey;
  }
  const resolved = resolveMainSessionKey(hello, sessions);
  if (resolved && !isSubagentSessionKey(resolved)) {
    return resolved;
  }
  for (const row of sessions?.sessions ?? []) {
    if (isSubagentSessionKey(row.key)) {
      continue;
    }
    const parsed = parseAgentSessionKey(row.key);
    if (parsed?.agentId === defaultAgentId && parsed.rest === "main") {
      return row.key;
    }
  }
  return `agent:${defaultAgentId}:main`;
}
